import {IconPlacement} from '@Components/button';
import {PostPublishStatus} from '@Libraries/social-media-publish-status.library';

export interface PostPublishStatusRowProps {
  status: PostPublishStatus;
  scheduledDateTimeLabel?: string;
  publishedActionLabel?: string;
  errorTitle?: string;
  errorDescription?: string;
  isActionDisabled?: boolean;
  onActionClick?: () => void;
  'data-testid'?: string;
}

export interface PostPublishStatusActionConfig {
  label: string;
  icon: string;
  iconPlacement: IconPlacement;
}

export interface ScheduledSubtitleRuns {
  textBeforeDateTime: string;
  textAfterDateTime: string;
}

export interface PostPublishStatusConfig {
  toneClass: PostPublishStatusTone;
  glyph: string;
  titleKey: string;
  subtitleKey: string;
  actionLabelKey: string;
  actionIcon: string;
  actionIconPlacement: IconPlacement;
}

export enum PostPublishStatusTone {
  SUCCESS = 'success',
  BRAND = 'brand',
  NEUTRAL = 'neutral',
  ERROR = 'error',
}

export const POST_PUBLISH_STATUS_CONFIG: Readonly<Record<PostPublishStatus, PostPublishStatusConfig>> = {
  [PostPublishStatus.PUBLISHING]: {
    toneClass: PostPublishStatusTone.BRAND,
    glyph: 'icon-loader',
    titleKey: 'pmwjs_social_publishing_your_posts',
    subtitleKey: 'pmwjs_social_publish_may_take_a_few_seconds',
    actionLabelKey: 'pmwjs_view_post',
    actionIcon: 'icon-external-link',
    actionIconPlacement: IconPlacement.RIGHT,
  },
  [PostPublishStatus.PUBLISHED]: {
    toneClass: PostPublishStatusTone.SUCCESS,
    glyph: 'icon-check',
    titleKey: 'pmwjs_published',
    subtitleKey: 'pmwjs_social_metrics_ready_in_a_few_hours',
    actionLabelKey: 'pmwjs_view_post',
    actionIcon: 'icon-external-link',
    actionIconPlacement: IconPlacement.RIGHT,
  },
  [PostPublishStatus.SCHEDULING]: {
    toneClass: PostPublishStatusTone.BRAND,
    glyph: 'icon-loader',
    titleKey: 'pmwjs_social_scheduling_your_posts',
    subtitleKey: 'pmwjs_social_publish_may_take_a_few_seconds',
    actionLabelKey: 'pmwjs_edit',
    actionIcon: 'icon-pencil',
    actionIconPlacement: IconPlacement.LEFT,
  },
  [PostPublishStatus.SCHEDULED]: {
    toneClass: PostPublishStatusTone.BRAND,
    glyph: 'icon-calendar',
    titleKey: 'pmwjs_scheduled',
    subtitleKey: 'pmwjs_social_post_scheduled_will_go_live_on',
    actionLabelKey: 'pmwjs_edit',
    actionIcon: 'icon-pencil',
    actionIconPlacement: IconPlacement.LEFT,
  },
  [PostPublishStatus.SAVING_DRAFT]: {
    toneClass: PostPublishStatusTone.NEUTRAL,
    glyph: 'icon-loader',
    titleKey: 'pmwjs_social_saving_as_draft',
    subtitleKey: 'pmwjs_social_publish_may_take_a_few_seconds',
    actionLabelKey: 'pmwjs_continue_editing',
    actionIcon: 'icon-pencil',
    actionIconPlacement: IconPlacement.LEFT,
  },
  [PostPublishStatus.SAVED_DRAFT]: {
    toneClass: PostPublishStatusTone.NEUTRAL,
    glyph: 'icon-save',
    titleKey: 'pmwjs_saved_as_draft',
    subtitleKey: 'pmwjs_social_continue_where_you_left_off',
    actionLabelKey: 'pmwjs_continue_editing',
    actionIcon: 'icon-pencil',
    actionIconPlacement: IconPlacement.LEFT,
  },
  [PostPublishStatus.FAILED]: {
    toneClass: PostPublishStatusTone.ERROR,
    glyph: 'icon-exclamation-triangle',
    titleKey: 'pmwjs_social_post_did_not_post',
    subtitleKey: '',
    actionLabelKey: 'pmwjs_social_fix_this',
    actionIcon: 'icon-refresh',
    actionIconPlacement: IconPlacement.LEFT,
  },
};

/** Splits the bold date/time run back out wherever a locale places it; a NUL never occurs in copy. */
export const SCHEDULED_DATE_TIME_SPLIT_MARKER = '\u0000';
